/**
 * Helpers for persisting per-category "collapsed" (minimized/maximized) state
 * inside the existing Layout document.
 *
 * The collapsed flag is stored on each category object in `layout.categories`.
 * The Layout schema already stores `layout` as an unconstrained Object, so we
 * can embed the flag without backend schema changes.
 */

/**
 * Extract a `{ [categoryId]: boolean }` map from a loaded layout.
 * Returns an empty object if the layout has no categories.
 */
export function getCollapsedIdsFromLayout(
	layout,
	forceCollapseAll = false
) {
	const categories = layout?.categories || [];
	const map = {};
	for (const cat of categories) {
		if (cat && cat.id) {
			if (forceCollapseAll) {
				map[cat.id] = true;
			} else if (typeof cat.collapsed === "boolean") {
				map[cat.id] = cat.collapsed;
			} else {
				map[cat.id] = !cat.cards || cat.cards.length === 0;
			}
		}
	}
	return map;
}

/**
 * Return a shallow-cloned layout where every category has its `collapsed` flag
 * set from the provided `{ [categoryId]: boolean }` map. Missing ids default
 * to `false` so persisted state mirrors the in-memory toggle state exactly.
 */
export function applyCollapsedStateToLayout(layout, collapsedCategoryIds) {
	if (!layout || typeof layout !== 'object') return layout;
	const map = collapsedCategoryIds || {};
	const categories = Array.isArray(layout.categories) ? layout.categories : [];
	return {
		...layout,
		categories: categories.map((cat) => ({
			...cat,
			collapsed: Boolean(map[cat?.id]),
		})),
	};
}
